/**
 * Browser file-download helpers for Docs Hub binaries.
 *
 * Downloads can't be plain `<a href>` links: the BFF and proxy require the
 * same auth and ontology-context headers RTK stamps on every other call, and
 * an anchor navigation sends neither. So we `fetch` the bytes with those
 * headers, wrap the response in a Blob, and trigger a save via a transient
 * object-URL anchor.
 */

import { getOntologyServiceBaseUrl } from '../../config'
import { PROXY_API_V1_URL, createOntologyHeaders, saveBlob } from '../core'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

async function fetchRevisionBlob(docId: string, revisionId: string): Promise<Blob> {
  const res = await fetch(
    `${PROXY_API_V1_URL}/docs/${encodeURIComponent(docId)}/revisions/${encodeURIComponent(revisionId)}/download`,
    { headers: createOntologyHeaders() }
  )
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`download failed (${res.status})${text ? `: ${text}` : ''}`)
  }
  return res.blob()
}

/** Download one revision's stored bytes, saving them under `fileName`. */
export async function downloadDocRevision(
  docId: string,
  revisionId: string,
  fileName: string
): Promise<void> {
  saveBlob(await fetchRevisionBlob(docId, revisionId), fileName)
}

/**
 * Fetch a revision's bytes (with the auth headers a bare `<iframe src>` can't
 * send) and return a transient object URL for in-browser preview — used for
 * PDFs, which the browser renders natively. The caller MUST revoke the URL
 * (`URL.revokeObjectURL`) when the preview unmounts to avoid leaking blobs.
 */
export async function docRevisionObjectUrl(docId: string, revisionId: string): Promise<string> {
  return URL.createObjectURL(await fetchRevisionBlob(docId, revisionId))
}

async function fetchLibraryBlob(docId: string): Promise<Blob> {
  const res = await fetch(`${ONTOLOGY_SERVICE_URL}/docs/${encodeURIComponent(docId)}/download`, {
    headers: createOntologyHeaders()
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`download failed (${res.status})${text ? `: ${text}` : ''}`)
  }
  return res.blob()
}

/**
 * Download a library document's current bytes (revision, with leftover
 * reports-download fallback on the BFF). Saves under `fileName`.
 */
export async function downloadLibraryDoc(docId: string, fileName: string): Promise<void> {
  saveBlob(await fetchLibraryBlob(docId), fileName)
}

/**
 * Object URL for a library document's current bytes (PDF preview). Caller
 * revokes. Uses the BFF so imported rows without sidecar bytes still load.
 */
export async function libraryDocObjectUrl(docId: string): Promise<string> {
  return URL.createObjectURL(await fetchLibraryBlob(docId))
}

async function download(path: string, fileName: string): Promise<void> {
  const res = await fetch(`${ONTOLOGY_SERVICE_URL}${path}`, { headers: createOntologyHeaders() })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`download failed (${res.status})${text ? `: ${text}` : ''}`)
  }
  saveBlob(await res.blob(), fileName)
}

async function objectUrl(path: string): Promise<string> {
  const res = await fetch(`${ONTOLOGY_SERVICE_URL}${path}`, { headers: createOntologyHeaders() })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`preview failed (${res.status})${text ? `: ${text}` : ''}`)
  }
  return URL.createObjectURL(await res.blob())
}

/** Download a document-template file, saving it under `fileName`. */
export function downloadDocTemplate(id: string, fileName: string): Promise<void> {
  return download(`/report-templates/${encodeURIComponent(id)}/download`, fileName)
}

/** Object URL for a document-template's bytes (PDF preview). Caller revokes. */
export function docTemplateObjectUrl(id: string): Promise<string> {
  return objectUrl(`/report-templates/${encodeURIComponent(id)}/download`)
}
